import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("password"); // "password" | "otp"

  // Password login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // OTP login state
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState(1); // 1=enter phone, 2=enter code
  const [resendIn, setResendIn] = useState(0);
  const [resending, setResending] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const switchMode = (m) => {
    setErr("");
    setMode(m);
    // reset OTP step when switching modes
    if (m === "password") {
      setOtpStep(1);
      setOtp("");
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name || u.email}`);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      setErr(e2.message);
    } finally { setLoading(false); }
  };

  const sendPhoneOtp = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login-otp-start", { phone });
      if (data.dev_code) toast.success(`DEV MODE OTP: ${data.dev_code}`);
      else toast.success("OTP sent to your phone");
      setOtpStep(2);
      setResendIn(30);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const resendPhoneOtp = async () => {
    if (resendIn > 0 || resending) return;
    setErr(""); setResending(true);
    try {
      const { data } = await api.post("/auth/login-otp-start", { phone });
      if (data.dev_code) toast.success(`DEV MODE OTP: ${data.dev_code}`);
      else toast.success("OTP re-sent to your phone");
      setResendIn(30);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setResending(false); }
  };

  const verifyPhoneOtp = async () => {
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login-otp-verify", { phone, code: otp });
      await refresh();
      toast.success(`Welcome back, ${data.name || data.email}`);
      navigate(data.role === "admin" ? "/admin" : "/dashboard");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const submitOtp = (e) => {
    e.preventDefault();
    if (otpStep === 1) return sendPhoneOtp();
    return verifyPhoneOtp();
  };

  const onGoogleAuth = async () => {
    // The button component already POSTed the credential and set our cookie.
    // Just refresh the auth context and route into the app.
    await refresh?.();
    toast.success("Signed in with Google");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between text-white p-12 relative overflow-hidden hero-grid-bg" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E3A8A 100%)" }}>
        <div className="absolute inset-0 hero-spotlight pointer-events-none" />
        <Link to="/" className="relative flex items-center gap-2.5" data-testid="login-logo">
          <div className="w-9 h-9 bg-brand-gold flex items-center justify-center rounded-lg shadow-lg">
            <Zap className="w-4 h-4 text-white" strokeWidth={3} />
          </div>
          <span className="font-black text-lg tracking-tight">StenoTypingBooster</span>
        </Link>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-white/60 mb-6">Log in</div>
          <h1 className="font-display text-5xl xl:text-6xl font-black tracking-tight leading-[1.05]">
            Master shorthand.<br />
            Type faster.<br />
            <span className="text-brand-gold">Clear your exam.</span>
          </h1>
          <p className="mt-6 max-w-md font-mono text-white/70 text-sm leading-relaxed">
            Continue your dictation streaks, legal drills and Pitman chapters — right where you left off.
          </p>
        </div>
        <div className="relative text-[10px] uppercase tracking-[0.4em] text-white/40 font-bold">Premium stenography examination platform</div>
      </div>

      <div className="flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-md">
          <div className="section-eyebrow">Welcome back</div>
          <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-2" style={{ color: "var(--brand-navy)" }}>Log in</h2>
          <p className="font-mono text-sm text-muted-foreground mb-6">Choose how you'd like to sign in.</p>

          {/* Google Sign-in — official GIS button, direct OAuth (not Emergent) */}
          <GoogleSignInButton onAuth={onGoogleAuth} text="signin_with" />
          <div className="relative mb-4 mt-2">
            <div className="h-px bg-border"></div>
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-background px-3 text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">or</div>
          </div>

          {/* Mode switcher */}
          <div className="grid grid-cols-2 border border-black/20 mb-6" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "password"}
              onClick={() => switchMode("password")}
              data-testid="login-mode-password"
              className={`h-11 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${
                mode === "password" ? "bg-[#0A2540] text-white" : "bg-white text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              Password
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "otp"}
              onClick={() => switchMode("otp")}
              data-testid="login-mode-otp"
              className={`h-11 text-xs uppercase tracking-[0.2em] font-bold transition-colors ${
                mode === "otp" ? "bg-[#0A2540] text-white" : "bg-white text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              Phone OTP
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={submitPassword} data-testid="login-form">
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-bold">Email</Label>
                  <Input
                    type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 rounded-none border-black/20 h-12 font-mono"
                    data-testid="login-email-input"
                    placeholder="you@example.com"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-bold">Password</Label>
                  <Input
                    type="password" required minLength={6}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 rounded-none border-black/20 h-12 font-mono"
                    data-testid="login-password-input"
                    placeholder="••••••••"
                  />
                </div>

                {err && (
                  <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="login-error">
                    {err}
                  </div>
                )}

                <Button
                  type="submit" disabled={loading}
                  className="w-full rounded-none h-12 bg-[#0A2540] hover:bg-[#002FA7] text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
                  data-testid="login-submit-btn"
                >
                  {loading ? "Logging in…" : "Log in"}
                </Button>

                <div className="text-right">
                  <Link to="/forgot-password" className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] hover:underline" data-testid="login-forgot-link">
                    Forgot password?
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            <form onSubmit={submitOtp} data-testid="login-otp-form">
              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-bold">Phone Number</Label>
                  <Input
                    type="tel" required minLength={7} maxLength={20}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={otpStep === 2}
                    className="mt-2 rounded-none border-black/20 h-12 font-mono"
                    data-testid="login-otp-phone-input"
                    placeholder="+91 98xxxxxxxx"
                  />
                </div>

                {otpStep === 2 && (
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] font-bold">OTP (6 digits)</Label>
                    <Input
                      required minLength={4} maxLength={8}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="123456"
                      className="mt-2 rounded-none border-black/20 h-12 font-mono text-center tracking-widest text-lg"
                      data-testid="login-otp-code-input"
                    />
                    <div className="mt-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => { setOtpStep(1); setOtp(""); setErr(""); setResendIn(0); }}
                        data-testid="login-otp-back"
                        className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-[#002FA7]"
                      >
                        ← Change phone
                      </button>
                      <button
                        type="button"
                        disabled={resendIn > 0 || resending}
                        onClick={resendPhoneOtp}
                        data-testid="login-otp-resend"
                        className="text-xs uppercase tracking-[0.2em] font-bold text-[#002FA7] disabled:text-neutral-400 disabled:cursor-not-allowed hover:underline"
                      >
                        {resendIn > 0 ? `Resend in ${resendIn}s` : resending ? "Sending…" : "Resend OTP"}
                      </button>
                    </div>
                  </div>
                )}

                {err && (
                  <div className="border-l-4 border-red-500 bg-red-50 px-4 py-2 text-sm font-mono text-red-700" data-testid="login-otp-error">
                    {err}
                  </div>
                )}

                <Button
                  type="submit" disabled={loading}
                  className="w-full rounded-none h-12 bg-[#0A2540] hover:bg-[#002FA7] text-white text-xs uppercase tracking-[0.2em] font-bold transition-transform hover:-translate-y-0.5"
                  data-testid="login-otp-submit-btn"
                >
                  {loading ? "Please wait…" : otpStep === 1 ? "Send OTP" : "Verify & Log in"}
                </Button>
              </div>
            </form>
          )}

          <div className="mt-8 text-sm font-mono text-neutral-500">
            No account?{" "}
            <Link to="/register" className="text-[#002FA7] font-bold underline underline-offset-4" data-testid="login-goto-register">
              Register here
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
